import type { ActivityDetail, Zone } from '@painpal/contracts';
import { useMemo } from 'react';
import { EChart } from '../../components/EChart';
import { LoadingBlock } from '../../components/StateViews';
import { hrModelFor } from '../../lib/zones';
import { useVariant } from '../../ui/useMediaQuery';
import { HR_CHART_HEIGHT, HR_CHART_LABEL, hrChartOption, hrChartStat, hrChartSub } from './hrChartOption';
import { useStreams } from './streams';
import styles from './analysis.module.css';

interface HrChartProps { activity: ActivityDetail; url: string; zone: Zone }

function HrChart({ activity, url, zone }: HrChartProps) {
  const streams = useStreams(activity.id, url);
  const phone = useVariant() === 'phone';
  const model = hrModelFor(zone.seconds.length);
  const option = useMemo(() => (streams.data === undefined ? null : hrChartOption(streams.data, zone, model, phone)), [streams.data, zone, model, phone]);
  const height = HR_CHART_HEIGHT[phone ? 'phone' : 'desktop'];
  return (
    <section className={styles.card} data-tone="strong" aria-labelledby="hr-chart-title">
      <header className={styles.curveHead}>
        <div className={styles.curveHeadText}>
          <h2 id="hr-chart-title" className={styles.cardTitle}>HEART RATE</h2>
          <p className={styles.curveSub}>{hrChartSub(zone, model)}</p>
        </div>
        <span className={styles.hrStat}>{hrChartStat(activity)}</span>
      </header>
      {streams.isPending && <LoadingBlock label="LOADING HEART RATE" height={height} />}
      {option !== null && <EChart option={option} height={height} label={HR_CHART_LABEL} />}
    </section>
  );
}

// The bands need the HR zones, so a session without an HR threshold gets no chart.
export function HrChartCard({ activity, url }: { activity: ActivityDetail; url: string }) {
  const zone = activity.zones.hr;
  return zone === undefined ? null : <HrChart activity={activity} url={url} zone={zone} />;
}
